/**
 * Guia do MGDLead — a leitura de um capítulo (`treinamentos/guia/:capituloId`).
 *
 * Desktop: navegação lateral fixa (todos os capítulos + as seções deste). Celular: chips dos
 * capítulos numa faixa que rola SÓ dentro dela (a página nunca rola de lado a 390 px) e um
 * sumário que abre. O fim do capítulo tem "Marcar como lido" e o próximo capítulo — é ali
 * que a pessoa está quando termina, e é ali que a próxima ação precisa estar.
 */
import { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { ArrowLeft, ArrowRight, BookOpen, Check, ChevronDown, Clock, Search } from 'lucide-react';
import { Button, EmptyState, cn } from '@mgdlead/ui';

import { contarPassos, minutosDeLeitura } from '../../../features/guia/busca';
import { IlustracaoGuia, PassoGuia, SeloLido } from '../../../features/guia/componentes';
import {
  CAPITULOS,
  GRUPOS_DO_GUIA,
  caminhoDoGuia,
  capituloPorId,
} from '../../../features/guia/conteudo';
import { useCapitulosLidos } from '../../../features/guia/progresso';
import type { CapituloDoGuia } from '../../../features/guia/tipos';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';

function dois(n: number): string {
  return String(n).padStart(2, '0');
}

/** Rola até a âncora (`#passo`) — o react-router não faz isso sozinho. */
function useAncora(capituloId: string | undefined) {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    const alvo = document.getElementById(decodeURIComponent(hash.slice(1)));
    alvo?.scrollIntoView?.({ block: 'start' });
  }, [hash, capituloId]);
}

function NavegacaoLateral({
  atual,
  slug,
  lidos,
}: {
  atual: CapituloDoGuia;
  slug: string;
  lidos: ReadonlySet<string>;
}) {
  return (
    <nav aria-label="Capítulos do guia" className="flex flex-col gap-5">
      <Link
        to={caminhoDoGuia(slug)}
        className="inline-flex items-center gap-2 rounded-pill bg-muted px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <Search aria-hidden="true" className="size-4" />
        Buscar no guia
      </Link>
      {GRUPOS_DO_GUIA.map((g) => (
        <div key={g.id} className="flex flex-col gap-1">
          <p className="px-2 font-mono text-2xs uppercase tracking-caps text-subtle-foreground">
            {g.titulo}
          </p>
          <ul className="flex flex-col">
            {g.capitulos.map((c) => {
              const ativo = c.id === atual.id;
              return (
                <li key={c.id}>
                  <Link
                    to={caminhoDoGuia(slug, c.id)}
                    aria-current={ativo ? 'page' : undefined}
                    className={cn(
                      'flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                      ativo
                        ? 'bg-surface-inverse text-inverse-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <span className="truncate">{c.titulo}</span>
                    {ativo ? null : <SeloLido lido={lidos.has(c.id)} />}
                  </Link>
                  {ativo ? (
                    <ul className="my-1 ml-3 flex flex-col border-l border-border pl-2">
                      {c.secoes.map((s) => (
                        <li key={s.id}>
                          <a
                            href={`#secao-${s.id}`}
                            className="block rounded-sm px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                          >
                            {s.titulo}
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function ChipsDeCapitulo({
  atual,
  slug,
  lidos,
}: {
  atual: CapituloDoGuia;
  slug: string;
  lidos: ReadonlySet<string>;
}) {
  const ref = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
  }, [atual.id]);
  return (
    <nav aria-label="Capítulos do guia" className="-mx-3 lg:hidden">
      <ul className="scrollbar-thin flex gap-2 overflow-x-auto px-3 pb-1">
        {CAPITULOS.map((c, i) => {
          const ativo = c.id === atual.id;
          return (
            <li key={c.id} className="shrink-0">
              <Link
                ref={ativo ? ref : undefined}
                to={caminhoDoGuia(slug, c.id)}
                aria-current={ativo ? 'page' : undefined}
                className={cn(
                  'inline-flex min-h-9 items-center gap-1.5 rounded-pill px-3 text-sm',
                  ativo
                    ? 'bg-surface-inverse text-inverse-foreground'
                    : 'bg-surface text-muted-foreground',
                )}
              >
                {lidos.has(c.id) && !ativo ? (
                  <Check aria-label="Lido" className="size-3.5 text-success" />
                ) : (
                  <span className="font-mono text-2xs nums opacity-70">{dois(i + 1)}</span>
                )}
                {c.titulo}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function GuiaCapituloPage() {
  const { capituloId } = useParams();
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const { lidos, marcar } = useCapitulosLidos();
  const capitulo = capituloPorId(capituloId);
  useDocumentTitle(capitulo ? `${capitulo.titulo} · Guia` : 'Guia do MGDLead');
  useAncora(capituloId);

  if (!capitulo) {
    return (
      <div className="rounded-lg bg-surface">
        <EmptyState
          kind="no-data"
          icon={BookOpen}
          title="Este capítulo não existe"
          description="O endereço pode ter mudado. Os capítulos estão todos no índice do guia."
          action={
            <Link to={caminhoDoGuia(workspace.slug)} className="text-sm font-medium text-primary-text">
              Abrir o índice do guia
            </Link>
          }
        />
      </div>
    );
  }

  const indice = CAPITULOS.findIndex((c) => c.id === capitulo.id);
  const anterior = indice > 0 ? CAPITULOS[indice - 1] : undefined;
  const proximo = CAPITULOS[indice + 1];
  const lido = lidos.has(capitulo.id);
  const Icone = capitulo.icone;

  return (
    <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-10">
      <aside className="hidden lg:block">
        <div className="scrollbar-thin sticky top-24 max-h-[calc(100dvh-7rem)] overflow-y-auto pb-4">
          <NavegacaoLateral atual={capitulo} slug={workspace.slug} lidos={lidos} />
        </div>
      </aside>

      <ChipsDeCapitulo atual={capitulo} slug={workspace.slug} lidos={lidos} />

      <article key={capitulo.id} className="guia-sobe flex min-w-0 flex-col gap-8">
        <header className="flex flex-col gap-4">
          <Link
            to={caminhoDoGuia(workspace.slug)}
            className="inline-flex w-fit items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft aria-hidden="true" className="size-4" />
            Guia do MGDLead
          </Link>
          <div className="flex items-start gap-4">
            <span className="hidden size-14 shrink-0 place-items-center rounded-lg bg-primary text-primary-foreground shadow-brand sm:grid">
              <Icone aria-hidden="true" className="size-7" />
            </span>
            <div className="flex min-w-0 flex-col gap-2">
              <p className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-2xs uppercase tracking-caps text-subtle-foreground">
                <span>
                  Capítulo {dois(indice + 1)} de {dois(CAPITULOS.length)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock aria-hidden="true" className="size-3" />
                  {minutosDeLeitura(capitulo)} min de leitura
                </span>
                <span>{contarPassos(capitulo)} tópicos</span>
                {lido ? (
                  <span className="inline-flex items-center gap-1 text-success">
                    <Check aria-hidden="true" className="size-3" />
                    lido
                  </span>
                ) : null}
              </p>
              <h1 className="text-2xl md:text-3xl">{capitulo.titulo}</h1>
              <p className="max-w-prose text-base text-muted-foreground">{capitulo.resumo}</p>
            </div>
          </div>

          <details className="group rounded-lg bg-surface lg:hidden">
            <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-2 px-4 text-sm font-medium">
              Neste capítulo
              <ChevronDown aria-hidden="true" className="size-4 transition-transform group-open:rotate-180" />
            </summary>
            <ol className="flex flex-col gap-1 px-4 pb-4">
              {capitulo.secoes.map((s, i) => (
                <li key={s.id}>
                  <a href={`#secao-${s.id}`} className="flex gap-2 py-1 text-sm text-muted-foreground">
                    <span className="font-mono text-xs nums text-subtle-foreground">{i + 1}.</span>
                    {s.titulo}
                  </a>
                </li>
              ))}
            </ol>
          </details>
        </header>

        {capitulo.capa ? <IlustracaoGuia imagem={capitulo.capa} prioridade /> : null}

        {capitulo.secoes.map((secao, i) => (
          <section
            key={secao.id}
            id={`secao-${secao.id}`}
            aria-labelledby={`titulo-${secao.id}`}
            className="flex scroll-mt-28 flex-col gap-6 rounded-xl bg-surface p-4 md:p-8"
          >
            <div className="flex flex-col gap-1 border-b border-border pb-4">
              <p className="font-mono text-2xs uppercase tracking-caps text-primary-text">
                Parte {i + 1}
              </p>
              <h2 id={`titulo-${secao.id}`} className="text-xl">
                {secao.titulo}
              </h2>
              {secao.resumo ? <p className="text-sm text-muted-foreground">{secao.resumo}</p> : null}
            </div>
            <ol className="flex flex-col">
              {secao.passos.map((p, k) => (
                <PassoGuia
                  key={p.id}
                  passo={p}
                  numero={k + 1}
                  ultimo={k === secao.passos.length - 1}
                />
              ))}
            </ol>
          </section>
        ))}

        <footer className="flex flex-col gap-3">
          <div className="flex flex-col items-start gap-3 rounded-xl bg-surface p-4 sm:flex-row sm:items-center sm:justify-between md:p-6">
            <div className="flex flex-col gap-0.5">
              <p className="text-md">{lido ? 'Capítulo lido' : 'Terminou este capítulo?'}</p>
              <p className="text-sm text-muted-foreground">
                A marca fica neste navegador e aparece no índice do guia.
              </p>
            </div>
            <Button
              variant={lido ? 'secondary' : 'primary'}
              leadingIcon={<Check />}
              onClick={() => {
                marcar(capitulo.id, !lido);
                if (!lido && proximo) navigate(caminhoDoGuia(workspace.slug, proximo.id));
              }}
              className="max-sm:w-full"
            >
              {lido ? 'Marcar como não lido' : proximo ? 'Marcar como lido e seguir' : 'Marcar como lido'}
            </Button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {anterior ? (
              <Link
                to={caminhoDoGuia(workspace.slug, anterior.id)}
                className="flex flex-col gap-1 rounded-lg bg-surface p-4 ring-1 ring-transparent hover:ring-border-strong"
              >
                <span className="inline-flex items-center gap-1 text-xs text-subtle-foreground">
                  <ArrowLeft aria-hidden="true" className="size-3.5" />
                  Capítulo anterior
                </span>
                <span className="text-md">{anterior.titulo}</span>
              </Link>
            ) : (
              <span className="hidden sm:block" />
            )}
            {proximo ? (
              <Link
                to={caminhoDoGuia(workspace.slug, proximo.id)}
                className="flex flex-col items-end gap-1 rounded-lg bg-surface p-4 text-right ring-1 ring-transparent hover:ring-border-strong"
              >
                <span className="inline-flex items-center gap-1 text-xs text-subtle-foreground">
                  Próximo capítulo
                  <ArrowRight aria-hidden="true" className="size-3.5" />
                </span>
                <span className="text-md">{proximo.titulo}</span>
              </Link>
            ) : (
              <Link
                to={caminhoDoGuia(workspace.slug)}
                className="flex flex-col items-end gap-1 rounded-lg bg-surface p-4 text-right ring-1 ring-transparent hover:ring-border-strong"
              >
                <span className="text-xs text-subtle-foreground">Fim do guia</span>
                <span className="text-md">Voltar ao índice</span>
              </Link>
            )}
          </div>
        </footer>
      </article>
    </div>
  );
}
